// tenant/ConfirmContactInfoPage.tsx
import React, { CSSProperties, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import TenantSignupLayout from '../TenantSignupLayout';

type LocationState = {
  facilityID: string;
  name: string;
  email: string;
  password: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  size: string;
  parking: string;
  building: string;
  // licenseNumber: string;
  // licenseState: string;
  minStay: string;
  typeOfPlan: string;
  price: string;
  referralCode?: string;
};

const baseFont: CSSProperties = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#222',
  fontSize: 14,
};

const labelStyle: CSSProperties = {
  ...baseFont,
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 6,
  border: '1px solid #CCCCCC',
  fontSize: 14,
  fontFamily: baseFont.fontFamily,
  boxSizing: 'border-box' as const,
};

const errorTextStyle: CSSProperties = {
  ...baseFont,
  fontSize: 12,
  color: '#d32f2f',
  marginTop: 4,
};

const hintTextStyle: CSSProperties = {
  ...baseFont,
  fontSize: 12,
  color: '#777',
  marginTop: 8,
};

const ConfirmContactInfoPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const state = location.state;

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[ConfirmContactInfoPage] route state:', state);
  }, [state]);

  // If someone hits this URL directly
  if (!state) {
    return (
      <TenantSignupLayout
        title="Confirm your contact info"
        subtitle="We need your account details before we can verify your code."
        step={4}
        totalSteps={7}
      >
        <div style={{ ...baseFont }}>
          <p style={{ marginBottom: 16 }}>
            It looks like you opened this page directly. Please go back to the beginning of signup.
          </p>
          <button
            type="button"
            onClick={() => history.push('/signup')}
            style={{
              ...baseFont,
              padding: '10px 16px',
              borderRadius: 6,
              border: 'none',
              backgroundColor: '#7B1FA2',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to signup
          </button>
        </div>
      </TenantSignupLayout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError('Please enter the confirmation code.');
      return;
    }

    try {
      setSubmitting(true);

      // 👉 Direct Cognito call (equivalent to mobile contactConfirmation)
      await Auth.confirmSignUp(state.email, code.trim());

      // Debug: verify everything is still there
      console.log('[ConfirmContactInfoPage] Confirmed. Pushing state to initial appointment:', state);

      // Push to the next step with all user data
      history.push('/signup/initial-appointment', state);
    } catch (err: any) {
      console.error('Error confirming account:', err);
      setError(err?.message || 'There was a problem confirming your code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TenantSignupLayout
      title="Check your email for a code"
      subtitle={`We sent a confirmation code to ${state.email}. Enter it below to verify your account.`}
      step={4}
      totalSteps={7}
    >
      <form onSubmit={handleSubmit} style={{ ...baseFont }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Confirmation code</label>
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="Enter the code from your email"
            style={inputStyle}
          />
          {error && <div style={errorTextStyle}>{error}</div>}
          <div style={hintTextStyle}>
            If you don’t see the email, check your spam folder or try resending from your mobile app.
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            ...baseFont,
            padding: '10px 16px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: '#7B1FA2',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
            textAlign: 'center' as const,
          }}
        >
          {submitting ? 'Confirming…' : 'Confirm account'}
        </button>
      </form>
    </TenantSignupLayout>
  );
};

export default ConfirmContactInfoPage;
